import { apiStore } from '$lib/stores/api';
import type { Axios } from 'axios';
import { get, writable, type Writable } from 'svelte/store';

export const infinityListStore = writable<InfinityListStore>({});
export const infinityItemListCountStore = writable<InfinityListItemCountStore>({});
export const infinityListReloadStore = writable<InfinityListReloadStore>({});
export const infinityListItemStore = writable<InfinityListItemStore>({});

// region Types
type InfinityListStore = {
	[key: string]: InfinityList;
};

type InfinityListItemStore = {
	[key: string]: unknown[];
};

type InfinityListItemCountStore = {
	[key: string]: {
		count: number;
		ended: boolean;
	};
};

type InfinityListReloadStore = {
	[key: string]: boolean;
};

export type InfinityList = {
	sort: InfinityListSort | null;
	filters: Array<AppliedFilter>;
	search: string | null;
};

export type InfinityListSort = {
	field: string;
	direction: 'asc' | 'desc' | null;
};

export type InfinityListFilters = {
	[key: string]: InfinityListFilter;
};

export type InfinityListFilter = {
	default: string | null;
	multiValue: boolean;
	items: Array<{ value: string | null; label?: string }>;
};

// Applied filters are the filters that are currently applied to the list with their value
export type AppliedFilter = {
	name: string;
	value: string | string[];
};

export type ListStore<T> = {
	data: Array<T>;
	isFresh: boolean;
	apiSearchUrl: string;
};

// endregion

export function getInfinityList(listId: string, sort: InfinityListSort | null = null) {
	const lists = get(infinityListStore);

	if (!lists[listId]) {
		lists[listId] = {
			sort,
			filters: [],
			search: null
		};
	}

	return lists[listId];
}

// region Filter Methods

export function hasNonDefaultFilter(listId: string, filters: InfinityListFilters) {
	const list = getInfinityList(listId);

	for (const key in filters) {
		const filter = getAppliedFilter(list, key);

		if (filter && filter.value !== filters[key].default) {
			return true;
		}
	}

	return false;
}

export function restoreDefaultFilters(listId: string, filters: InfinityListFilters) {
	const list = getInfinityList(listId);

	for (const key in filters) {
		const filter = getAppliedFilter(list, key);

		if (filter && filter.value !== filters[key].default) {
			unsetInfinityFilter(listId, key);
		}
	}
}

/**
 * Get the desired applied filter from the list
 * @param list The list to get the filter from
 * @param filterName The name of the filter to get
 */
export function getAppliedFilter(list: InfinityList, filterName: string): AppliedFilter | null {
	if (!list) {
		return null;
	}

	return list.filters.find((f) => f.name === filterName) || null;
}

/**
 * Remove a filter from the filter list
 */
export function unsetInfinityFilter(listId: string, key: string) {
	const list = get(infinityListStore)[listId];

	if (list) {
		infinityListStore.update((store) => {
			store[listId].filters = store[listId].filters.filter((f) => f.name !== key);
			return store;
		});
	}
}

/**
 * Define a filter in the filter list
 * If no value is provided, the filter will be considered as a flag
 */
export function setInfinityFilter(listId: string, key: string, value?: string | string[]) {
	const list = getInfinityList(listId);
	let filter = getAppliedFilter(list, key);

	if (!filter) {
		// Init and define if doesn't exists
		filter = { name: key };

		if (value) {
			filter.value = value;
		}

		infinityListStore.update((store) => {
			if (filter) {
				store[listId].filters = [...store[listId].filters, filter];
			}
			return store;
		});

		return;
	}

	if (value) {
		infinityListStore.update((store) => {
			store[listId].filters = store[listId].filters.map((f) => {
				if (f.name === key) {
					f.value = value;
				}
				return f;
			});
			return store;
		});
	}
}

/**
 * Add a value to a value filter
 */
export function addInfinityFilterValue(listId: string, key: string, value: string) {
	const list = getInfinityList(listId);
	const filter = getAppliedFilter(list, key);

	if (!filter) {
		setInfinityFilter(listId, key, [value]);
		return;
	}

	if (filter && filter.value) {
		if (!Array.isArray(filter.value)) {
			console.error('Cannot add value to a flag filter');
			return;
		}

		if (!filter.value.includes(value)) {
			infinityListStore.update((store) => {
				store[listId].filters = store[listId].filters.map((f) => {
					if (f.name === key) {
						f.value = [...(f.value as string[]), value];
					}
					return f;
				});
				return store;
			});
		}
	}
}

/**
 * Remove a value from a value filter
 */
export function removeInfinityFilterValue(listId: string, key: string, value: string) {
	const list = getInfinityList(listId);
	const filter = getAppliedFilter(list, key);

	if (!filter) {
		setInfinityFilter(listId, key, [value]);
		return;
	}

	if (filter && filter.value) {
		if (!Array.isArray(filter.value)) {
			console.error('Cannot remove value to a flag filter');
			return;
		}

		if (filter.value.includes(value)) {
			infinityListStore.update((store) => {
				store[listId].filters = store[listId].filters.map((f) => {
					if (f.name === key) {
						f.value = (f.value as string[]).filter((v) => v !== value);
					}
					return f;
				});
				return store;
			});
		}
	}
}

// endregion

// region List Freshness Methods
/**
 * Refresh a given item in an infinity list, by fetching it again from the API.
 * @param itemId The id of the item to refresh
 * @param itemIdKey The key of the item id in the item object
 * @param store The store to refresh the item in
 * @param options Options for the refresh
 * @param options.apiToAppItemTransformer A callback to transform the item from the API to the app
 */
export function refreshItemInList<T>(
	itemId: string | number,
	itemIdKey: keyof T,
	store: Writable<ListStore<T>>,
	options: {
		apiToAppItemTransformer?: (item: T) => T;
	} = {}
) {
	const storeData = get(store);

	// Only refresh if the item is currently in the store data
	if (!storeData.data.find((item) => item[itemIdKey] === itemId)) {
		return;
	}

	// Fetch the updated item
	(get(apiStore) as Axios)
		.post(storeData.apiSearchUrl, {
			id: itemId
		})
		.then((response) => {
			// Replace the element
			let item = response.data[0];
			if (options.apiToAppItemTransformer && item) {
				item = options.apiToAppItemTransformer(item);
			}

			store.update((store) => {
				const index = store.data.findIndex(
					(currentItem) => currentItem[itemIdKey] === itemId
				);

				if (!item) {
					// The item was not found, on the api, surely because it was removed
					// Remove it from the store
					store.data.splice(index, 1);
				} else {
					// The item was found, update it
					store.data[index] = item;
				}

				return store;
			});
		});
}

/**
 * Mark a list as not fresh unless the item exists in the list.
 * @param itemId The id of the item to check
 * @param itemIdKey The key of the item id in the item object
 * @param store The store to mark as not fresh
 */
export function markListAsNotFreshUnlessExists<T>(
	itemId: string | number,
	itemIdKey: keyof T,
	store: Writable<ListStore<T>>
) {
	const storeData = get(store);

	// Only mark as not fresh if the item is NOT currently in the store data
	if (storeData.data.find((item) => item[itemIdKey] === itemId)) {
		return;
	}

	store.update((store) => {
		store.isFresh = false;
		return store;
	});
}

// endregion
